/**
 * Shared ArcGIS REST query helper — rate-limit aware retries, response
 * cache, and bounded concurrency. Used by regional layer fetch, USFWS
 * environmental provider, and other ArcGIS consumers.
 */
import {
  RETRYABLE_HTTP_STATUS,
  parseRetryAfterMs,
} from '../../lib/httpRetry';
import { abortReason, abortableDelay, throwIfAborted } from '../../lib/abortSignal';
import { fetchWithTimeout } from '../dataProviders/types';
import type { EsriFeatureResponse } from './esriJsonToSiteLayers';

const DEFAULT_TIMEOUT_MS = 25_000;
const MAX_ATTEMPTS = 4;
const CACHE_TTL_MS = 30 * 60 * 1000;
const MAX_CONCURRENT = 1;

/** Client-side token bucket — 3 requests per 15s (conservative for ArcGIS Online). */
let tokens = 3;
let lastRefill = Date.now();
const CAPACITY = 3;
const REFILL_MS = 15_000;

let inFlight = 0;
const waitQueue: Array<() => void> = [];

const responseCache = new Map<string, { json: EsriFeatureResponse; ts: number }>();

export function isArcgisRateLimitMessage(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('too many requests') ||
    m.includes('rate limit') ||
    m.includes('unable to perform query') && m.includes('too many')
  );
}

function takeToken(): boolean {
  const now = Date.now();
  if (now - lastRefill >= REFILL_MS) {
    tokens = CAPACITY;
    lastRefill = now;
  }
  if (tokens <= 0) return false;
  tokens -= 1;
  return true;
}

async function acquireToken(signal?: AbortSignal): Promise<void> {
  for (let i = 0; i < 6; i++) {
    throwIfAborted(signal);
    if (takeToken()) return;
    const wait = Math.max(500, REFILL_MS - (Date.now() - lastRefill));
    await abortableDelay(Math.min(wait, 4000), signal);
  }
  throw new Error('ArcGIS rate limit — try again in a few seconds');
}

async function acquireSlot(signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  if (inFlight < MAX_CONCURRENT) {
    inFlight += 1;
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const grant = () => {
      signal?.removeEventListener('abort', onAbort);
      inFlight += 1;
      resolve();
    };
    const onAbort = () => {
      const index = waitQueue.indexOf(grant);
      if (index >= 0) waitQueue.splice(index, 1);
      signal?.removeEventListener('abort', onAbort);
      reject(abortReason(signal));
    };
    waitQueue.push(grant);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function releaseSlot(): void {
  inFlight = Math.max(0, inFlight - 1);
  const next = waitQueue.shift();
  if (next) next();
}

function cacheGet(url: string): EsriFeatureResponse | null {
  const hit = responseCache.get(url);
  if (!hit) return null;
  if (Date.now() - hit.ts > CACHE_TTL_MS) {
    responseCache.delete(url);
    return null;
  }
  return hit.json;
}

function cacheSet(url: string, json: EsriFeatureResponse): void {
  responseCache.set(url, { json, ts: Date.now() });
  if (responseCache.size > 200) {
    const oldest = responseCache.keys().next().value;
    if (oldest) responseCache.delete(oldest);
  }
}

function arcgisRetryWaitMs(
  attempt: number,
  status: number,
  retryAfterHeader: string | null | undefined,
): number {
  const retryAfter = parseRetryAfterMs(retryAfterHeader);
  if (retryAfter != null && retryAfter > 0) return retryAfter;
  const is429 = status === 429;
  const base = is429 ? 2_000 : 500;
  const cap = is429 ? 60_000 : 8_000;
  const exp = Math.min(base * 2 ** (attempt - 1), cap);
  const jitter = exp * (0.8 + Math.random() * 0.4);
  return Math.round(jitter);
}

export interface QueryArcgisRestOptions {
  timeoutMs?: number;
  label?: string;
  /** Skip response cache (e.g. tests). */
  noCache?: boolean;
  /** Attempt-owned report cancellation/watchdog signal. */
  signal?: AbortSignal;
}

/**
 * Fetch a fully-formed ArcGIS REST query URL and parse the JSON envelope.
 * Retries transient HTTP failures and ArcGIS "Too many requests" errors.
 */
export async function queryArcgisRest(
  url: string,
  opts: QueryArcgisRestOptions = {},
): Promise<EsriFeatureResponse> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const label = opts.label ?? 'ArcGIS';
  const signal = opts.signal;
  throwIfAborted(signal);

  if (!opts.noCache) {
    const cached = cacheGet(url);
    if (cached) return cached;
  }

  await acquireSlot(signal);
  try {
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      throwIfAborted(signal);
      await acquireToken(signal);

      const res = await fetchWithTimeout(url, timeoutMs, signal);
      throwIfAborted(signal);

      if (!res.ok) {
        const retryable = RETRYABLE_HTTP_STATUS.has(res.status) || res.status === 429;
        if (retryable && attempt < MAX_ATTEMPTS) {
          const waitMs = arcgisRetryWaitMs(attempt, res.status, res.headers.get('Retry-After'));
          console.warn(
            `[arcgis:${label}] HTTP ${res.status} attempt ${attempt}/${MAX_ATTEMPTS} — retrying in ${waitMs}ms`,
          );
          await abortableDelay(waitMs, signal);
          continue;
        }
        throw new Error(`${label}: HTTP ${res.status}`);
      }

      const json = (await res.json()) as EsriFeatureResponse;
      if (json.error) {
        const msg = json.error.message ?? String(json.error.code ?? 'unknown');
        if (isArcgisRateLimitMessage(msg) && attempt < MAX_ATTEMPTS) {
          const waitMs = arcgisRetryWaitMs(attempt, 429, null);
          console.warn(
            `[arcgis:${label}] rate limited attempt ${attempt}/${MAX_ATTEMPTS} — retrying in ${waitMs}ms`,
          );
          await abortableDelay(waitMs, signal);
          continue;
        }
        throw new Error(`${label} ArcGIS error: ${msg}`);
      }

      throwIfAborted(signal);
      if (!opts.noCache) cacheSet(url, json);
      return json;
    }

    throw new Error(`${label}: retry loop exhausted`);
  } finally {
    releaseSlot();
  }
}

/** Test helper — reset module state between tests. */
export function resetArcgisQueryStateForTests(): void {
  tokens = CAPACITY;
  lastRefill = Date.now();
  inFlight = 0;
  waitQueue.length = 0;
  responseCache.clear();
}
